import { TimelineCitationContext, type AssistantCitationRequest } from "./timelineCitationContext";
import { useTimelineCitationTarget } from "./useTimelineCitationTarget";
import { TimelineCitationActions } from "./TimelineCitationActions";
import { useEffect, useRef, useState, type RefObject } from "react";
import { LegendList, type LegendListProps, type LegendListRef } from "@legendapp/list/react";
import type { MessagesTimelineRow } from "./MessagesTimeline.logic";
import { useThreadTimelineScroll } from "./useThreadTimelineScroll";
import { deriveTimelineMinimapItems, type TimelineMinimapItem } from "./timelineMinimapItems";
import { TimelineTurnNavigation } from "./TimelineTurnNavigation";

export interface TimelineViewportProps {
  readonly rows: MessagesTimelineRow[];
  readonly citationRequest?: AssistantCitationRequest | null;
  readonly listRef: RefObject<LegendListRef | null>;
  readonly routeThreadKey: string;
  readonly timelineInset: number;
  readonly onIsAtEndChange: (atEnd: boolean) => void;
  readonly renderItem: NonNullable<LegendListProps<MessagesTimelineRow>["renderItem"]>;
}

/** Owns the list viewport; durable reading anchors stay in useThreadTimelineScroll. */
export function TimelineViewport(props: TimelineViewportProps) {
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [followEnabled, setFollowEnabled] = useState(true);
  const navigationFrame = useRef<number | null>(null);
  const navigating = useRef(false);
  const navigationGeneration = useRef(0);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const items = deriveTimelineMinimapItems(props.rows);
  const { initialPosition, scrollHandlers, anchor, cancelPendingRestore } =
    useThreadTimelineScroll(props);
  const updateCurrentTurn = () => {
    if (items.length === 0) return;
    const state = props.listRef.current?.getState?.();
    if (!state || typeof state.positionAtIndex !== "function") return;
    let current = items[0];
    for (const item of items) {
      const top = state.positionAtIndex(item.rowIndex);

      if (top >= state.scroll + state.scrollLength) break;
      current = item;
      if (top + Math.max(1, state.sizeAtIndex(item.rowIndex)) > state.scroll) break;
    }
    if (current) setCurrentId(current.id);
  };
  useEffect(() => {
    if (!viewport) return;
    const measure = () => setViewportWidth(viewport.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [viewport]);
  const navigate = (item: TimelineMinimapItem) => {
    const index = props.rows.findIndex((row) => row.id === item.id);
    if (index < 0) return;
    cancelPendingRestore();
    props.onIsAtEndChange(false);
    setFollowEnabled(false);
    setCurrentId(item.id);
    navigating.current = true;
    const generation = ++navigationGeneration.current;
    if (navigationFrame.current !== null) cancelAnimationFrame(navigationFrame.current);
    // Apply the follow pause before virtual rows resize during the jump.
    navigationFrame.current = requestAnimationFrame(() => {
      navigationFrame.current = null;
      const animated = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const result = props.listRef.current?.scrollToIndex({ index, viewOffset: 24, animated });
      const finishNavigation = () => {
        if (generation !== navigationGeneration.current) return;
        navigating.current = false;
        setFollowEnabled(props.listRef.current?.getState().isAtEnd === true);
        updateCurrentTurn();
      };
      void Promise.resolve(result).then(finishNavigation, finishNavigation);
    });
  };
  useEffect(
    () => () => {
      navigationGeneration.current += 1;
      if (navigationFrame.current !== null) cancelAnimationFrame(navigationFrame.current);
    },
    [],
  );

  const citation = useTimelineCitationTarget({
    request: props.citationRequest ?? null,
    rows: props.rows,
    viewport,
    pauseFollowing: () => {
      cancelPendingRestore();
      setFollowEnabled(false);
      props.onIsAtEndChange(false);
    },
  });

  const onScroll = () => {
    scrollHandlers.onScroll();
    updateCurrentTurn();
    if (!followEnabled && !navigating.current && props.listRef.current?.getState?.().isAtEnd)
      setFollowEnabled(true);
  };

  return (
    <TimelineCitationContext value={{ target: citation.target, listRef: props.listRef }}>
      <div
        ref={setViewport}
        data-assistant-citation-viewport="true"
        className="relative flex h-full min-h-0 min-w-0 flex-col"
        onWheelCapture={cancelPendingRestore}
        onTouchMoveCapture={cancelPendingRestore}
        onPointerDownCapture={cancelPendingRestore}
        onKeyDownCapture={cancelPendingRestore}
      >
        <TimelineCitationActions viewport={viewport} routeThreadKey={props.routeThreadKey} />
        <TimelineTurnNavigation
          items={items}
          currentId={currentId}
          viewportWidth={viewportWidth}
          onSelect={navigate}
        />
        <div className="min-h-0 flex-1">
          <TimelineList
            viewportProps={props}
            initialPosition={initialPosition}
            anchor={anchor}
            followEnabled={followEnabled}
            alwaysRender={citation.alwaysRender}
            onMetricsChange={scrollHandlers.onMetricsChange}
            onLoad={() => {
              updateCurrentTurn();
              citation.onLoad();
            }}
            onScroll={onScroll}
          />
        </div>
      </div>
    </TimelineCitationContext>
  );
}

function TimelineList({
  viewportProps,
  initialPosition,
  anchor,
  followEnabled,
  alwaysRender,
  onMetricsChange,
  onLoad,
  onScroll,
}: {
  readonly viewportProps: TimelineViewportProps;
  readonly initialPosition: ReturnType<typeof useThreadTimelineScroll>["initialPosition"];
  readonly anchor: ReturnType<typeof useThreadTimelineScroll>["anchor"];
  readonly followEnabled: boolean;
  readonly alwaysRender: ReturnType<typeof useTimelineCitationTarget>["alwaysRender"];
  readonly onMetricsChange: ReturnType<
    typeof useThreadTimelineScroll
  >["scrollHandlers"]["onMetricsChange"];
  readonly onLoad: () => void;
  readonly onScroll: () => void;
}) {
  return (
    <LegendList<MessagesTimelineRow>
      ref={viewportProps.listRef}
      data={viewportProps.rows}
      renderItem={viewportProps.renderItem}
      keyExtractor={(item) => item.id}
      estimatedItemSize={90}
      recycleItems={false}
      contentInsetEndAdjustment={viewportProps.timelineInset}
      initialScrollAtEnd={initialPosition?.isAtEnd ?? true}
      initialScrollOffset={initialPosition?.offset ?? 0}
      {...(anchor === null ? {} : { initialScrollIndex: anchor })}
      maintainScrollAtEnd={followEnabled}
      maintainScrollAtEndThreshold={0.1}
      maintainVisibleContentPosition
      onMetricsChange={onMetricsChange}
      {...(alwaysRender ? { alwaysRender: alwaysRender } : {})}
      onLoad={onLoad}
      onScroll={onScroll}
      className="h-full overflow-x-hidden overscroll-y-contain px-3 sm:px-5"
      ListHeaderComponent={<div className="h-3 sm:h-4" />}
      ListFooterComponent={<div className="h-3 sm:h-4" />}
    />
  );
}
